export function telify(v) {
  return v
    .replace('(', '')
    .replace(')', '')
    .replace('-', '')
    .replace(/\s/g, '');
}

export function maxstache(str, ctx) {
  return str
    .split(/\{|\}/)
    .map((t, i) => (!(i % 2) ? t : ctx[t]))
    .join('');
}

export const imageSizes = [600, 800, 1200, 1600, 2000, 2400, 3000];

/**
 * Converts a string from dash or underscore separated to camelCase.
 *
 * @param  {String} str
 * @return {String}
 */
export function camelCase(str) {
  // remove any dashes or underscores in the string, and grab the following character
  return str.replace(/[-_]+(\w?)/g, (_, p1) => {
    // uppercase the following letter
    return p1.toUpperCase();
  });
}

/**
 * Gets all the attributes set on an element, and returns them as an Object.
 *
 * @param  {Element} element
 * @return {Object}
 */
export function getAttributes(element) {
  const attrs = {};

  // if element is empty, or not ELEMENT_NODE, eject
  if (!element || element.nodeType !== 1) return attrs;

  // grab all the attributes off the element
  const map = element.attributes;

  // if there are no attributes, eject
  if (map.length === 0) return attrs;

  // loop through the attributes and build the object
  for (var i = 0; i < map.length; i++) {
    attrs[map[i].name] = map[i].value;
  }

  return attrs;
}

/**
 * Searches an element's attributes and returns an Object of all the ones that
 * begin with a specified prefix. Each matching attribute name is returned
 * without the prefix, with the remainder converted to camelCase.
 *
 * @param  {Element} element
 * @param  {String} prefix
 * @return {Object}
 */
export function getMatchingAttributes(element, prefix) {
  // get all the attributes on the element
  const attributes = getAttributes(element);
  const fields = {};

  // loop through the keys of the attributes
  Object.keys(attributes).forEach(function(key) {
    // continue if the key begins with supplied prefix
    if (key.substr(0, prefix.length) === prefix) {
      // grab the value associated with the original key
      const value = attributes[key];

      // slice off the prefix and camelCase what is left
      const field = camelCase(key.slice(prefix.length));

      fields[field] = value;
    }
  });

  return fields;
}

/**
 * Counter used by {uniqueId} to iterate.
 * @private
 * @type {Number}
 */
let idCounter = 0;

/**
 * Generates identifiers unique to a single browser session. Each time it runs,
 * the integer at the end of the return value will increment.
 *
 * Borrowed from underscore.js: http://underscorejs.org/#uniqueId
 *
 * @param  {String} [prefix] Optional prefix for identifier
 * @return {String}
 * @example
 *
 * var identifier = uniqueId();
 * // returns '1'
 *
 * var prefixedIdentifier = uniqueId('label-');
 * // returns 'label-2'
 */
export function uniqueId(prefix) {
  const id = ++idCounter + '';
  return prefix ? prefix + id : id;
}

/**
 * A helper for asynchronously loading scripts.
 *
 * Provides a Promise interface for passing a function that will only be called
 * if the script is successfully added to the page.
 *
 * @private
 * @param {String} url The URL for the script to be loaded.
 * @returns {Promise}
 * @example
 *
 * loadScript('backup.js').then(() => {
 *   // anything that depends on that script loading
 * });
 */
export function loadScript(url) {
  return new Promise((resolve, reject) => {
    // create the `script` element
    const script = document.createElement('script');

    // set its URL
    script.src = url;

    // should always be async
    script.async = true;

    // resolve the Promise on load
    script.onload = resolve;

    // reject the Promise if there is an error
    script.onerror = reject;

    // attach the script to the document body
    document.body.appendChild(script);
  });
}

export function debounce(func, wait, immediate) {
  var timeout, args, context, timestamp, result;

  var later = function() {
    var last = Date.now() - timestamp;

    if (last < wait && last >= 0) {
      timeout = setTimeout(later, wait - last);
    } else {
      timeout = null;
      if (!immediate) {
        result = func.apply(context, args);
        if (!timeout) context = args = null;
      }
    }
  };

  return function() {
    context = this;
    args = arguments;
    timestamp = Date.now();
    var callNow = immediate && !timeout;
    if (!timeout) timeout = setTimeout(later, wait);
    if (callNow) {
      result = func.apply(context, args);
      context = args = null;
    }

    return result;
  };
}

/**
 * Removes the element.
 * @param {Element} element
 */
export function removeElement(element) {
  if (element.parentElement) {
    element.parentElement.removeChild(element);
  }
}

/**
 * Removes all child nodes of the specified element.
 * @param {Element} parent
 */
export function removeChildren(parent) {
  while (parent.firstChild) {
    parent.removeChild(parent.firstChild);
  }
}

/**
 * Pre-defined regex for parsing out a cookie's value.
 *
 * @private
 * @type {RegExp}
 */
const cookieRe = /(%[\dA-F]{2})+/gi;

/**
 * Decodes a portion of a cookie's value with a regex customized for cookie
 * strings.
 *
 * @private
 * @param {string} value
 */
function decodeCookieValue(value) {
  return value.replace(cookieRe, decodeURIComponent);
}

/**
 * Returns the value of the provided cookie from the document, if it exists.
 * Otherwise returns "undefined".
 *
 * @param {string} name
 * @returns {string|undefined}
 */
export function getCookie(name) {
  // if there is no document, there are no cookies
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookies = document.cookie ? document.cookie.split('; ') : [];

  for (let idx = 0; idx < cookies.length; idx++) {
    // the raw individual cookie string
    const raw = cookies[idx];

    // break the cookie into parts
    const parts = raw.split('=');

    // prepare the key
    const key = decodeCookieValue(parts[0]);

    // we have a match!
    if (key === name) {
      // put the cookie back together again
      let value = parts.slice(1).join('=');

      // if the value is wrapped in quotes, peel them off
      if (value[0] === '"') {
        value = value.slice(1, -1);
      }

      // return the decoded value
      return decodeCookieValue(value);
    }
  }

  // never found a match
  return undefined;
}
